import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'src/auth/auth-context';
import { MessageModal } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ICommandConfiguration } from 'src/common/interfaces/dtos/ICommandsGroup';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { authenticateCommand } from 'src/menu-panel/business-logic/post-authenticate-command';
import {
  AuthenticateCommandDto,
  authenticateTlog,
} from 'src/menu-panel/business-logic/post-authenticate-tlog';
import { popupAsyncMessage } from 'src/messages/business-logic/popup-message';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import { styles } from './styles';
import { showCommandSupervisorApprovalDialog } from './supervisor-approval-commands';

const CommandNeedsPermissionModal: React.FC<CommandNeedsPermissionModalProps> = ({
  resolve,
  reject,
  commandId,
  signOffState,
  insufficientPrivileges,
}) => {
  const { t } = useTranslation();
  const { signinPopup } = useAuthContext();
  const classes = styles();

  const handleLogin = useCallback(() => {
    signinPopup()
      .then(user => {
        resolve(user.access_token);
      })
      .finally(() => {
        popEmeraldModal(SECURITY_APPROVAL_REQUIRED_MODAL_ID);
      });
  }, [signinPopup, resolve]);

  const onClose = () => {
    popEmeraldModal(SECURITY_APPROVAL_REQUIRED_MODAL_ID);
    reject();
  };

  const getApprovalSubMessage = (
    <Grid container justifyContent='center'>
      <Grid item>
        <Typography>{t('SecurityOnCommandsLogin_IAM_Submessage1')}</Typography>
      </Grid>
      <Grid item className={classes.commandLine}>
        <Typography data-testid='show-permissions-modal-command-name'>{t(commandId)}</Typography>
      </Grid>
      <Grid item>
        <Typography>{t('SecurityOnCommandsLogin_IAM_Submessage2')}</Typography>
      </Grid>
    </Grid>
  );

  const insufficientPrivilegesSubMessage = (
    <Grid container justifyContent='center'>
      <Grid item>
        <Typography>{t('InsufficientPrivileges_Retry_SubMessage')}</Typography>
      </Grid>
      <Grid item className={classes.commandLine}>
        <Typography data-testid='show-permissions-modal-command-name'>{t(commandId)}</Typography>
      </Grid>
    </Grid>
  );

  const commonProps = {
    onAccept: handleLogin,
    onCancel: onClose,
    separateButton: true,
  };

  const getApprovalProps = {
    ...commonProps,
    message: 'SupervisorApprovalCaption',
    subMessage: getApprovalSubMessage,
    acceptButtonLabel: 'GetApproval',
  };

  const insufficientPrivilegesProps: IMessageModal = {
    ...commonProps,
    message: 'InsufficientPrivileges_Message',
    subMessage: insufficientPrivilegesSubMessage,
    acceptButtonLabel: 'Retry',
    type: EmeraldDialogTypes.Error,
  };

  const modalProps = insufficientPrivileges ? insufficientPrivilegesProps : getApprovalProps;

  useEffect(() => {
    if (signOffState) {
      handleLogin();
    }
  }, [signOffState, handleLogin]);

  if (signOffState) {
    return <></>;
  }

  return <MessageModal {...modalProps} />;
};

interface CommandNeedsPermissionModalProps {
  resolve: (token: string) => void;
  reject: () => void;
  commandId: string;
  signOffState: boolean;
  insufficientPrivileges: boolean;
}

const SECURITY_APPROVAL_REQUIRED_MODAL_ID = 'security-approval-required-modal-id';

const showApprovalRequiredModal = (
  commandId: string,
  signOffState: boolean,
  insufficientPrivileges: boolean,
): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    pushEmeraldModal(
      <CommandNeedsPermissionModal
        {...{ resolve, reject, commandId, signOffState, insufficientPrivileges }}
      />,
      undefined,
      SECURITY_APPROVAL_REQUIRED_MODAL_ID,
    );
  });
};

const showCloudApprovalRequiredModal = (
  command: ICommandConfiguration,
  signOffState: boolean = false,
  insufficientPrivileges: boolean = false,
): Promise<ILoginUser> => {
  let modalId: string | undefined = undefined;
  return new Promise<ILoginUser>((resolve, reject) => {
    modalId = SECURITY_APPROVAL_REQUIRED_MODAL_ID;
    showCommandSupervisorApprovalDialog(
      {
        command: command,
        resolve,
        reject,
        insufficientPrivileges,
        signOffState,
      },
      modalId,
    );
  });
};

export const checkCommandPermission = async (
  command: ICommandConfiguration,
  signOffState: boolean = false,
  insufficientPrivileges: boolean = false,
): Promise<ILoginUser | undefined> => {
  const { EDGE_IAM } = getMetaData().xlrConfig;

  let creds: ILoginUser = {};
  try {
    if (EDGE_IAM) {
      creds.sessionId = await showApprovalRequiredModal(
        command.name || '',
        signOffState,
        insufficientPrivileges,
      );
    } else {
      creds = await showCloudApprovalRequiredModal(command, signOffState, insufficientPrivileges);
    }
  } catch {
    return undefined;
  }
  try {
    const authValue = await authenticateCommand(command, creds, creds.sessionId);

    const tlogCreds: AuthenticateCommandDto = {
      approverId: authValue.approverId,
      approverRole: authValue.approverRole,
      actionLabel: command.name,
    };

    if (signOffState) {
      await authenticateTlog(tlogCreds, creds.sessionId);
    } else {
      await authenticateTlog(tlogCreds);
    }

    return creds;
  } catch {
    if (signOffState && EDGE_IAM) {
      await insufficientPrivilegesModal();
      return undefined;
    } else {
      return await checkCommandPermission(command, signOffState, true);
    }
  } finally {
    if (!insufficientPrivileges) {
      popEmeraldModal(SECURITY_APPROVAL_REQUIRED_MODAL_ID);
    }
  }
};

export const insufficientPrivilegesModal = async (): Promise<void> => {
  const span = createSpan('insufficient-privileges');
  const insufficientPrivilegesMessage: IMessageModal = {
    message: 'InsufficientPrivileges_Message',
    subMessage: 'InsufficientPrivileges_SubMessage',
    type: EmeraldDialogTypes.Error,
    hideCancelButton: true,
    onAccept: () => {
      endSpan(span);
    },
    acceptButtonLabel: 'Ok',
  };
  popupAsyncMessage(insufficientPrivilegesMessage);
};
